import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import {
  BaseButton,
  CloseButton,
  Input,
  Toast,
  useAuth,
  useIsMobile,
  useT,
} from '@repo/common';
import {
  confirmVerificationCode,
  sendVerificationCode,
} from '../../../../api/user/user.api';

import { ReactComponent as ShieldSecurityIcon } from '@repo/common/assets/icons/24px/icon_24px_shield-security-basic_filled.svg';
import { ReactComponent as InfoIcon } from '@repo/common/assets/icons/20px/icon_20px_info_filled.svg';

const EmailVerifyCodeModal = ({ email, onClose }) => {
  const t = useT();
  const { refreshUser } = useAuth();
  const isMobile = useIsMobile();
  const successImage = `${process.env.REACT_APP_ASSETS_URL}/MyPage/img-mypage-email-verify-success.svg`;
  const [codes, setCodes] = useState(['', '', '', '']);
  const [isResending, setIsResending] = useState(false);
  const [resendToastKey, setResendToastKey] = useState(0);
  const [showResendToast, setShowResendToast] = useState(false);
  const [verifySuccess, setVerifySuccess] = useState(false);
  const [verifyError, setVerifyError] = useState(false);

  const [isOpen, setIsOpen] = useState(false);

  const inputRefs = useMemo(
    () => [
      React.createRef(),
      React.createRef(),
      React.createRef(),
      React.createRef(),
    ],
    [],
  );

  useEffect(() => {
    setTimeout(() => {
      inputRefs[0].current?.querySelector('input')?.focus();
    }, 50);
  }, [inputRefs]);

  useEffect(() => {
    const id = requestAnimationFrame(() => setIsOpen(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const handleClose = () => {
    if (!isMobile) {
      onClose();
      return;
    }
    setIsOpen(false);
    setTimeout(onClose, 250);
  };

  const handleChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const newCodes = [...codes];
    newCodes[index] = digit;
    setCodes(newCodes);

    if (digit && index < 3) {
      inputRefs[index + 1].current?.querySelector('input')?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !codes[index] && index > 0) {
      inputRefs[index - 1].current?.querySelector('input')?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, 4);
    const newCodes = [...codes];
    pasted.split('').forEach((digit, i) => {
      if (i < 4) newCodes[i] = digit;
    });
    setCodes(newCodes);
    const lastIndex = Math.min(pasted.length, 3);
    inputRefs[lastIndex].current?.querySelector('input')?.focus();
  };

  const handleVerify = async () => {
    try {
      await confirmVerificationCode(email, fullCode);
      setVerifySuccess(true);
    } catch (err) {
      console.error('이메일 인증 실패', err);
      setVerifyError(true);
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    try {
      await sendVerificationCode(email);
      setResendToastKey((prev) => prev + 1);
      setShowResendToast(true);
    } catch (err) {
      console.error('재전송 실패', err);
    } finally {
      setIsResending(false);
    }
  };

  const handleDone = async () => {
    await refreshUser();
    handleClose();
  };

  const fullCode = codes.join('');
  const isComplete = fullCode.length === 4;

  const content = verifySuccess ? (
    <div className='flex flex-col items-center w-full'>
      <div className='w-full pb-4'>
        <div className='flex justify-start tabletH:justify-end'>
          <CloseButton
            type={isMobile ? 'light' : 'default'}
            onClick={handleClose}
          />
        </div>
        <div className='w-full'>
          <img alt='successImage' src={successImage} />
        </div>
      </div>

      <div className='flex flex-col items-center gap-1 text-center pb-[60px] tabletH:pb-[84px] w-full'>
        <div className='typo-title1 text-text-primary'>
          {t('Email verified!', '이메일 인증 완료!')}
        </div>
        <div className='typo-b12 text-text-secondary'>
          {t(
            'Your email has been successfully verified',
            '이메일 인증이 성공적으로 완료되었습니다',
          )}
        </div>
      </div>

      <BaseButton size='l' hideIcon width='100%' onClick={handleDone}>
        {t('Done', '완료')}
      </BaseButton>
      {isMobile && <div className='h-[33px] w-full' />}
    </div>
  ) : (
    <>
      <div className='flex flex-col text-center w-full pb-6'>
        {isMobile && (
          <div className='flex justify-start w-full pb-2'>
            <CloseButton type='light' onClick={handleClose} />
          </div>
        )}
        <div
          className={`flex items-start w-full pb-2 ${
            isMobile ? 'justify-center' : 'justify-between'
          }`}
        >
          {!isMobile && <div className='flex-1' />}
          <div
            className={`flex justify-center items-center ${
              isComplete ? 'bg-green-light02' : 'bg-grayscale-gray02'
            } rounded-[200px] w-15 h-15 p-2.5 aspect-square`}
          >
            <div
              className={`${
                isComplete ? 'bg-functional-success03' : 'bg-grayscale-gray03'
              } rounded-[200px] w-10 h-10 p-2 aspect-square`}
            >
              <ShieldSecurityIcon className='text-icon-inverse' />
            </div>
          </div>
          {!isMobile && (
            <div className='flex-1 flex justify-end'>
              <CloseButton type='default' onClick={handleClose} />
            </div>
          )}
        </div>
        <div className='flex flex-col items-center gap-0.5 text-center'>
          <div className='typo-b20Strong text-text-primary'>
            {t('Enter verification code', '인증 코드를 입력해 주세요')}
          </div>
          <div className='flex gap-0.5'>
            <div className='typo-b12 text-text-secondary'>
              {t('We sent a code to', '다음 주소로 인증 코드를 보냈습니다:')}
            </div>
            <div className='typo-b12Strong text-text-primary'>{email}</div>
          </div>
        </div>
      </div>

      {/* 코드 입력 4칸 */}
      <div className='flex flex-col justify-center items-center gap-2 pt-6 py-11 w-full'>
        <div className='flex justify-center items-center gap-2'>
          {codes.map((code, index) => (
            <div key={index} ref={inputRefs[index]}>
              <Input
                label=''
                placeholder=''
                value={code}
                padding='p-3'
                height='h-[54px]'
                className='w-[48px]'
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                maxLength={1}
                hideCount
                style={{ textAlign: 'center' }}
              />
            </div>
          ))}
        </div>
        {verifyError && (
          <div className='flex typo-b14Strong text-text-failed gap-0.5'>
            <InfoIcon />
            {t('Invalid code. Please try again.', '잘못된 코드입니다. 다시 시도해 주세요.')}
          </div>
        )}
      </div>

      <div className='w-full flex flex-col'>
        <BaseButton
          size='l'
          hideIcon
          width='100%'
          isDisabled={!isComplete}
          onClick={handleVerify}
        >
          {t('Verify', '인증하기')}
        </BaseButton>

        <div className='flex justify-center items-center typo-b12 py-2 gap-1'>
          <div className='text-text-secondary'>
            {t("Didn't get a code?", '코드를 받지 못하셨나요?')}
          </div>
          <button
            className='text-text-primary underline hover:text-text-tertiary'
            onClick={handleResend}
            disabled={isResending}
          >
            {t('Click to resend', '재전송하기')}
          </button>
        </div>

        {showResendToast && (
          <div
            key={resendToastKey}
            className='absolute bottom-full left-0 right-0 px-4 mb-6 z-[1000] flex justify-center'
          >
            <Toast
              type='success'
              state='white'
              title={t('Verification code sent.', '인증 코드가 전송되었습니다.')}
              direction='down'
              customWidth='auto'
            />
          </div>
        )}
      </div>
    </>
  );

  if (isMobile) {
    return ReactDOM.createPortal(
      <div className='fixed inset-0 z-[1000000000] flex items-end justify-center'>
        <div
          className={`absolute inset-0 bg-surface-black40 transition-opacity duration-[250ms] ${
            isOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={handleClose}
        />

        <div
          className='relative w-full bg-grayscale-gray00 rounded-t-[40px] px-6 pb-10 pt-4
          flex flex-col items-center max-h-[90vh] overflow-y-auto
          transition-transform duration-[250ms] ease-out'
          style={{ transform: isOpen ? 'translateY(0)' : 'translateY(100%)' }}
          onClick={(e) => e.stopPropagation()}
        >
          {content}
        </div>
      </div>,
      document.body,
    );
  }

  return ReactDOM.createPortal(
    <div className='fixed inset-0 z-[1000000000] flex items-center justify-center bg-surface-black60'>
      <div className='absolute inset-0' onClick={handleClose} />

      <div
        className='relative bg-grayscale-gray00 border-grayscale-gray02 rounded-[30px] p-6 w-[420px]
        flex flex-col items-center shadow-popup-02'
        onClick={(e) => e.stopPropagation()}
      >
        {content}
      </div>
    </div>,
    document.body,
  );
};

export default EmailVerifyCodeModal;
